import React, { useState } from 'react';
import classNames from 'classnames';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import './NoticeCenter.scss';

// Most urgent first. `critical` is for deadlines and blocking issues,
// `action` for something the seller has to confirm, `warning` for things
// that will become a problem if ignored.
const SEVERITY_ORDER = ['critical', 'action', 'warning', 'info'];
const SEVERITY_ICONS = {
  critical: 'error-hexagon',
  action: 'check-circle',
  warning: 'warning-hexagon',
  info: 'info-hexagon',
};
const DISMISSED_KEY = 'notice-dismissed:';

const isDismissed = (id) => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY + id) === '1';
  } catch (e) {
    return false;
  }
};

const rememberDismissed = (id) => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY + id, '1');
  } catch (e) {
    // Storage blocked: the notice just comes back on the next page load.
  }
};

const Notice = ({ notice, onDismiss }) => (
  <div className={classNames('notice', `notice--${notice.severity}`)} role={notice.severity === 'critical' ? 'alert' : 'status'}>
    <span className="notice__icon" aria-hidden="true">
      <CsLineIcons icon={notice.icon || SEVERITY_ICONS[notice.severity]} size={18} />
    </span>
    <div className="notice__body">
      <div className="notice__title">{notice.title}</div>
      {notice.message && <div className="notice__message">{notice.message}</div>}
    </div>
    {notice.action && <div className="notice__action">{notice.action}</div>}
    {notice.dismissible && (
      <button type="button" className="notice__dismiss" aria-label={`Dismiss: ${notice.title}`} onClick={() => onDismiss(notice.id)}>
        <CsLineIcons icon="close" size={14} />
      </button>
    )}
  </div>
);

/**
 * Seller hub notices shown under the top nav, in one card. When there are
 * several, only the most urgent is shown until the seller expands the list.
 * @param {Object[]} notices - { id, severity, title, message?, action?, icon?, dismissible? }
 */
const NoticeCenter = ({ notices = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(() => notices.filter((n) => n.dismissible && isDismissed(n.id)).map((n) => n.id));

  const visible = notices
    .filter((n) => !(n.dismissible && (dismissed.includes(n.id) || isDismissed(n.id))))
    .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
  if (!visible.length) return null;

  const shown = expanded ? visible : visible.slice(0, 1);
  const hiddenCount = visible.length - shown.length;

  const handleDismiss = (id) => {
    rememberDismissed(id);
    setDismissed([...dismissed, id]);
  };

  return (
    <section className="notice-center container" aria-label="Notifications">
      <div className="notice-center__card">
        {shown.map((notice) => (
          <Notice key={notice.id} notice={notice} onDismiss={handleDismiss} />
        ))}
        {visible.length > 1 && (
          <button type="button" className="notice-center__toggle" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show less' : `Show ${hiddenCount} more notification${hiddenCount === 1 ? '' : 's'}`}
            <CsLineIcons icon={expanded ? 'chevron-top' : 'chevron-bottom'} size={13} />
          </button>
        )}
      </div>
    </section>
  );
};

export default NoticeCenter;
